import axios from "axios";
import { appConfig } from "@quest/appConfig";
import { store } from "@quest/store";
// @ts-ignore
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { getPublicIP } from "./get-public-ip";

let isLoggingOutDueToExpiredSession = false;
let refreshAccessTokenPromise: Promise<string> | null = null;

const isSecureOrigin =
  typeof window !== "undefined" && window.location.protocol === "https:";

const authCookieOptions = {
  path: "/",
  secure: isSecureOrigin,
  sameSite: "Strict" as const,
};

// Cookies must be removed with the same path/attrs they were set with.
// Login sets without sameSite; refresh sets with authCookieOptions — clear both.
const clearSessionCookies = () => {
  Cookies.remove("accessToken", authCookieOptions);
  Cookies.remove("refreshToken", authCookieOptions);
  Cookies.remove("accessToken");
  Cookies.remove("refreshToken");
  Cookies.remove("from");
  localStorage.removeItem("authToken");
  localStorage.removeItem("sessionToken");
};

const isOnAuthPage = () =>
  window.location.pathname.includes("/login") ||
  window.location.pathname.includes("/register");

const expireSession = () => {
  if (isLoggingOutDueToExpiredSession) return;

  clearSessionCookies();
  if (isOnAuthPage()) return;

  isLoggingOutDueToExpiredSession = true;
  toast.error("Your session has expired. Please log in again.", {
    position: "top-right",
    autoClose: 2500,
  });
  window.setTimeout(() => window.location.replace("/login"), 2500);
};

const refreshAccessToken = (refreshToken: string) => {
  if (!refreshAccessTokenPromise) {
    const tenant = store.getState().tenant;

    refreshAccessTokenPromise = refreshAxiosInstance
      .post(
        "/tenant/auth/refresh",
        { refresh_token: refreshToken },
        {
          withCredentials: true,
          headers: {
            "x-tenant-id": tenant.keyCloakOrganizationUUID,
            "x-tenant-name": tenant.slug_name,
            "x-client-id": tenant.keyCloakClientUUID,
          },
        }
      )
      .then((response) => {
        const accessToken = response.data?.data?.access_token;

        if (!accessToken) {
          throw new Error("Refresh response did not include an access token");
        }

        Cookies.set("accessToken", accessToken, authCookieOptions);
        return accessToken;
      })
      .finally(() => {
        refreshAccessTokenPromise = null;
      });
  }

  return refreshAccessTokenPromise;
};

// Create a separate axios instance for refresh token requests..
const refreshAxiosInstance = axios.create({
  baseURL: `${appConfig.apiUrl}/api/v1`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Main axios instance
const schoolAxiosInstance = axios.create({
  baseURL: `${appConfig.apiUrl}/api/v1`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

schoolAxiosInstance.interceptors.request.use(async (config) => {
  const ip = await getPublicIP();
  config.headers["x-header-ip"] = ip;
  return config;
});

refreshAxiosInstance.interceptors.request.use(async (config) => {
  const ip = await getPublicIP();
  config.headers["x-header-ip"] = ip;
  return config;
});

// Request interceptor
schoolAxiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get("accessToken");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    const tenant = store.getState().tenant;
    const tenantId = tenant.keyCloakOrganizationUUID;
    const tenantName = tenant.slug_name;
    const clientId = tenant.keyCloakClientUUID;

    if (tenantId && tenantName) {
      config.headers["x-tenant-id"] = tenantId;
      config.headers["x-tenant-name"] = tenantName;
      config.headers["x-client-id"] = clientId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const isViewAsPreviewActive = () =>
  Boolean(store.getState().preview?.active);

schoolAxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Several page queries can fail together. While refreshing or logging out,
    // none of the remaining failures may override the session flow with a
    // forbidden or service-unavailable redirect.
    if (isLoggingOutDueToExpiredSession || refreshAccessTokenPromise) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const refreshToken = Cookies.get("refreshToken");

      try {
        if (!refreshToken) throw new Error("No refresh token");
        const newAccessToken = await refreshAccessToken(refreshToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return schoolAxiosInstance(originalRequest);
      } catch (refreshError) {
        expireSession();
        return Promise.reject(refreshError);
      }
    } else if (error?.response?.status === 401) {
      // A retried request that is still unauthorized is an expired session,
      // not a missing admin route. Never leave a stale token behind.
      expireSession();
    } else if (error.response?.data?.error === "Service Unavailable") {
      window.location.href = "/";
    } else if (error.response?.status === 403) {
      // View-as keeps the staff JWT; student-only mutations return 403.
      // Do not bounce staff to /admin/forbidden mid-preview.
      if (!isViewAsPreviewActive()) {
        window.location.href = "/admin/forbidden";
      }
    }

    return Promise.reject(error);
  }
);

export default schoolAxiosInstance;
